/**
 * Bulk Pricing Utility
 * Handles quantity-based pricing rules and discounts
 */

// Default bulk pricing tiers
export const DEFAULT_BULK_PRICING_TIERS = [
  { minQty: 1, maxQty: 4, discount: 0, label: 'Regular Price' },
  { minQty: 5, maxQty: 9, discount: 5, label: '5% Off' },
  { minQty: 10, maxQty: 19, discount: 10, label: '10% Off' },
  { minQty: 20, maxQty: 49, discount: 15, label: '15% Off' },
  { minQty: 50, maxQty: 99, discount: 20, label: '20% Off' },
  { minQty: 100, maxQty: Infinity, discount: 25, label: '25% Off' }
];

/**
 * Get bulk pricing tier for a given quantity
 * @param {number} quantity - Item quantity
 * @param {Array} tiers - Custom pricing tiers (optional)
 * @returns {Object} Pricing tier object
 */
export const getBulkPricingTier = (quantity, tiers = DEFAULT_BULK_PRICING_TIERS) => {
  if (!quantity || quantity < 1) {
    return tiers[0];
  }
  
  return tiers.find(tier => quantity >= tier.minQty && quantity <= tier.maxQty) || tiers[tiers.length - 1];
};

/**
 * Calculate price with bulk discount
 * @param {number} basePrice - Base price per unit
 * @param {number} quantity - Item quantity
 * @param {Array} tiers - Custom pricing tiers (optional)
 * @returns {Object} { unitPrice, totalPrice, discount, savings, tier }
 */
export const calculateBulkPrice = (basePrice, quantity, tiers = DEFAULT_BULK_PRICING_TIERS) => {
  if (!basePrice || !quantity || quantity < 1) {
    return {
      unitPrice: basePrice || 0,
      totalPrice: (basePrice || 0) * (quantity || 1),
      discount: 0,
      savings: 0,
      tier: tiers[0]
    };
  }

  const tier = getBulkPricingTier(quantity, tiers);
  const discountPercent = tier.discount || 0;
  const unitPrice = basePrice * (1 - discountPercent / 100);
  const totalPrice = unitPrice * quantity;
  const originalTotal = basePrice * quantity;
  const savings = originalTotal - totalPrice;

  return {
    unitPrice: Math.round(unitPrice * 100) / 100,
    totalPrice: Math.round(totalPrice * 100) / 100,
    discount: discountPercent,
    savings: Math.round(savings * 100) / 100,
    tier: tier
  };
};

/**
 * Get next tier information for quantity increase
 * @param {number} currentQuantity - Current item quantity
 * @param {Array} tiers - Custom pricing tiers (optional)
 * @returns {Object|null} Next tier info or null if already at max tier
 */
export const getNextTier = (currentQuantity, tiers = DEFAULT_BULK_PRICING_TIERS) => {
  const currentTier = getBulkPricingTier(currentQuantity, tiers);
  const currentTierIndex = tiers.findIndex(t => t.minQty === currentTier.minQty);
  
  if (currentTierIndex < tiers.length - 1) {
    const nextTier = tiers[currentTierIndex + 1];
    const qtyNeeded = nextTier.minQty - currentQuantity;
    return {
      ...nextTier,
      qtyNeeded: qtyNeeded,
      additionalSavings: (currentTier.discount || 0) < (nextTier.discount || 0) 
        ? nextTier.discount - currentTier.discount 
        : 0
    };
  }
  
  return null;
};

/**
 * Format bulk pricing display
 * @param {number} basePrice - Base price
 * @param {number} quantity - Quantity
 * @param {Array} tiers - Custom pricing tiers (optional)
 * @returns {String} Formatted price string
 */
export const formatBulkPrice = (basePrice, quantity, tiers = DEFAULT_BULK_PRICING_TIERS) => {
  const pricing = calculateBulkPrice(basePrice, quantity, tiers);
  return {
    unitPrice: `₹${pricing.unitPrice.toFixed(2)}`,
    totalPrice: `₹${pricing.totalPrice.toFixed(2)}`,
    savings: pricing.savings > 0 ? `Save ₹${pricing.savings.toFixed(2)}` : null,
    discount: pricing.discount > 0 ? `${pricing.discount}% OFF` : null
  };
};

